import * as React from 'react'

import {
  FieldArray,
  Formik,
  type FormikErrors,
  FormikValues,
  useFormik,
  useFormikContext,
} from 'formik'

import type { Result } from 'modules/core/lib/handleMutation'

export type FormErrors<FormValues> = FormikErrors<FormValues>

type Entity<FormValues> = Partial<Record<keyof FormValues, unknown>>
type FormArguments<FormValues extends FormikValues> = Parameters<
  typeof useFormik<FormValues>
>[number]

type OnSubmitFn<FormValues extends FormikValues> = FormArguments<FormValues>['onSubmit']

type FormParams<FormValues extends FormikValues> = {
  defaultValues: FormValues
  entity?: Entity<FormValues>
  validate?: FormArguments<FormValues>['validate']
  validateOnChange?: boolean
  validateOnBlur?: boolean
  validateOnMount?: boolean
  enableReinitialize?: boolean
  onSubmit: (...args: Parameters<OnSubmitFn<FormValues>>) => Promise<Result<unknown>>
}

function buildInitialValues<FormValues extends object>(
  defaultValues: FormValues,
  entity?: Entity<FormValues>
) {
  const setValues = Object.keys(defaultValues).reduce<Partial<FormValues>>((memo, key) => {
    const value = entity ? entity[key as keyof FormValues] : undefined

    if (value === undefined) return memo

    return {
      ...memo,
      [key]: value,
    }
  }, {})

  return {
    ...defaultValues,
    ...setValues,
  }
}

export function useForm<FormValues extends object>({
  defaultValues,
  entity,
  validate,
  enableReinitialize,
  validateOnChange = true,
  validateOnBlur = true,
  validateOnMount = false,
  onSubmit,
}: FormParams<FormValues>) {
  const initialValues = buildInitialValues(defaultValues, entity)

  return useFormik<FormValues>({
    initialValues,
    validate,
    enableReinitialize,
    validateOnChange,
    validateOnBlur,
    validateOnMount,
    onSubmit,
  })
}

export function FormProvider<FormValues extends object>({
  defaultValues,
  entity,
  validate,
  enableReinitialize,
  validateOnChange,
  validateOnBlur,
  onSubmit,
  children,
  validateOnMount,
}: React.PropsWithChildren<FormParams<FormValues>>) {
  const initialValues = buildInitialValues(defaultValues, entity)

  return (
    <Formik
      enableReinitialize={enableReinitialize ?? false}
      validateOnChange={validateOnChange ?? true}
      validateOnBlur={validateOnBlur ?? true}
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
      validateOnMount={validateOnMount}
    >
      {children}
    </Formik>
  )
}

export const useFormContext = useFormikContext

export { FieldArray }
